import Accordion from "@/components/Accordion";
import { LanguageConfigModel, StyleConfigModel } from "@/pages/Messaging/types";
import { Toggle } from "@/components/Toggle";
import React from "react";
import TextInput from "../../TextInput";
import { ChatWindow } from "../../types";
import FeatureHint from "@/components/FeatureHint";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
}

const STYLES: { id: "bubble" | "corner" | "inline"; label: string; description: string }[] = [
  { id: "bubble", label: "Bubble", description: "Floating video bubble next to launcher" },
  { id: "corner", label: "Corner launcher", description: "Replaces the chat launcher icon" },
  { id: "inline", label: "Inline in chat", description: "Plays inside the chat window as a card" },
];

export function AvatarLauncher(props: Props) {
  const style = props.workingChatWindow.styleConfig || {};
  const lang  = props.workingChatWindow.languageConfig || {};

  const updateStyle = (u: Partial<StyleConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, styleConfig: { ...style, ...u } });
  const updateLang = (u: Partial<LanguageConfigModel>) =>
    props.setWorkingChatWindow({ ...props.workingChatWindow, languageConfig: { ...lang, ...u } });

  const activeStyle = style.avatarLauncherStyle ?? "bubble";

  return (
    <Accordion.Item eventKey="avatar-launcher" onExpanded={() => props.onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Video Avatar</Accordion.Title>
        <Accordion.SubTitle>
          Greet visitors with an AI video avatar — powered by your bot's voice + persona
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div className="mb-2">
          <div className="flex justify-between items-center">
            <div className="text-sm font-medium">Enable</div>
            <Toggle
              toggled={!!style.avatarLauncherEnabled}
              changeToggle={() => updateStyle({ avatarLauncherEnabled: !style.avatarLauncherEnabled })}
            />
          </div>

          {style.avatarLauncherEnabled && (
            <>
              <div className="my-3 border-t border-slate-200" />
              <div className="text-sm font-medium mb-2">Launcher style</div>
              <div className="grid gap-2">
                {STYLES.map(s => (
                  <label
                    key={s.id}
                    className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer ${activeStyle === s.id ? "border-violet-400 bg-violet-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <input
                      type="radio"
                      name="avatarLauncherStyle"
                      checked={activeStyle === s.id}
                      onChange={() => updateStyle({ avatarLauncherStyle: s.id })}
                      className="mt-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]"
                    />
                    <div>
                      <div className="text-sm font-medium">{s.label}</div>
                      <div className="text-xs text-slate-500">{s.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="my-3 border-t border-slate-200" />
              <div className="text-sm font-medium my-2">Launcher label</div>
              <TextInput
                value={lang.avatarLauncherLabel ?? ""}
                setValue={v => updateLang({ avatarLauncherLabel: v })}
                showEmojiButton={false}
              />
              <div className="text-sm font-medium my-2">Greeting (spoken on open)</div>
              <TextInput
                value={lang.avatarLauncherGreeting ?? ""}
                setValue={v => updateLang({ avatarLauncherGreeting: v })}
                showEmojiButton={false}
              />

              <div className="my-3" />
              <FeatureHint
                hintKey="avatar-workflow-handoff"
                severity="tip"
                title="Hand off to avatar from any workflow"
                body="Add a 'Hand off to Avatar' node in the workflow canvas to transition mid-conversation — keeps the bot's persona and language."
                cta={{ label: "Open workflow designer →", to: "/Messaging/Workflows" }}
              />
            </>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
